import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { storefrontCategories } from '../data/storefrontContent';
import './Products.css';

const CATEGORY_TITLES = {
  all: 'Product Showcase',
  racquets: 'Racquets Collection',
  strings: 'Strings Type',
  accessories: 'Accessories',
};

const CATEGORY_INTRO_COPY = {
  racquets: 'Frames chosen for speed, control, and confident attacking play.',
  strings: 'Choose by feel, repulsion, thickness, and badminton-specific response.',
  accessories: 'Finish your setup with the add-ons players replace and rebuy often.',
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categoryTitle, setCategoryTitle] = useState('Product Showcase');
  const [filterText, setFilterText] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [stringTypeFilter, setStringTypeFilter] = useState('');
  const [racquetTypeFilter, setRacquetTypeFilter] = useState('');
  const [gaugeFilter, setGaugeFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [stringBrands, setStringBrands] = useState([]);
  const [stringPage, setStringPage] = useState(1);
  const [stringTotalPages, setStringTotalPages] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();

  const debounceRef = useRef(null);
  const selectedCategoryRef = useRef(selectedCategory);
  const sortOptionRef = useRef(sortOption);
  const stringTypeFilterRef = useRef(stringTypeFilter);
  const racquetTypeFilterRef = useRef(racquetTypeFilter);
  const gaugeFilterRef = useRef(gaugeFilter);
  const brandFilterRef = useRef(brandFilter);

  selectedCategoryRef.current = selectedCategory;
  sortOptionRef.current = sortOption;
  stringTypeFilterRef.current = stringTypeFilter;
  racquetTypeFilterRef.current = racquetTypeFilter;
  gaugeFilterRef.current = gaugeFilter;
  brandFilterRef.current = brandFilter;

  const fetchProducts = async (category, filter, sort, strType, racquetType, gauge, brand, page = 1) => {
    setLoading(true);

    try {
      let url;

      if (category === 'strings') {
        url = `${API_BASE_URL}/strings?page=${page}&limit=20`;
        if (filter) url += `&search=${encodeURIComponent(filter)}`;
        if (strType) url += `&type=${encodeURIComponent(strType)}`;
        if (racquetType) url += `&racquet_type=${encodeURIComponent(racquetType)}`;
        if (gauge) url += `&gauge=${encodeURIComponent(gauge)}`;
        if (brand) url += `&brand=${encodeURIComponent(brand)}`;
      } else {
        url = `${API_BASE_URL}/products?category=${category}&limit=50`;
        if (filter) url += `&search=${encodeURIComponent(filter)}`;
        if (sort) {
          const sortMap = {
            price_asc: { sortBy: 'price', sortOrder: 'asc' },
            price_desc: { sortBy: 'price', sortOrder: 'desc' },
            name_asc: { sortBy: 'name', sortOrder: 'asc' },
            id_desc: { sortBy: 'id', sortOrder: 'desc' },
          };
          const params = sortMap[sort];
          if (params) {
            url += `&sortBy=${params.sortBy}&sortOrder=${params.sortOrder}`;
          }
        }
      }

      const response = await fetch(url);
      const data = await response.json();
      setProducts(data.products || []);

      if (category === 'strings') {
        setStringBrands(data.brands || []);
        setStringTotalPages(data.pagination?.totalPages || 1);
        setStringPage(page);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const enterCategory = (category) => {
    clearTimeout(debounceRef.current);
    setFilterText('');
    setSortOption('');
    setStringTypeFilter('');
    setRacquetTypeFilter('');
    setGaugeFilter('');
    setBrandFilter('');
    setStringPage(1);
    setStringTotalPages(1);
    setSelectedCategory(category);
    setCategoryTitle(CATEGORY_TITLES[category] || 'Products');
    fetchProducts(category, '', '', '', '', '', '', 1);
  };

  const handleFilterChange = (value) => {
    setFilterText(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProducts(
        selectedCategoryRef.current,
        value,
        sortOptionRef.current,
        stringTypeFilterRef.current,
        racquetTypeFilterRef.current,
        gaugeFilterRef.current,
        brandFilterRef.current,
        1
      );
    }, 300);
  };

  const handleSortChange = (value) => {
    setSortOption(value);
    fetchProducts(
      selectedCategoryRef.current,
      filterText,
      value,
      stringTypeFilterRef.current,
      racquetTypeFilterRef.current,
      gaugeFilterRef.current,
      brandFilterRef.current,
      1
    );
  };

  const handleStringPageChange = (newPage) => {
    fetchProducts(
      selectedCategoryRef.current,
      filterText,
      sortOptionRef.current,
      stringTypeFilterRef.current,
      racquetTypeFilterRef.current,
      gaugeFilterRef.current,
      brandFilterRef.current,
      newPage
    );
  };

  useEffect(() => {
    const restoreCategory = location.state?.restoreCategory;

    if (restoreCategory && restoreCategory !== 'all') {
      enterCategory(restoreCategory);
    } else {
      setLoading(false);
    }

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderCategoryGateway = () => (
    <>
      <section className="products-hero">
        <div className="page-header storefront-page-header">
          <span className="products-kicker">Public storefront</span>
          <h1>Shop by Category</h1>
          <p>
            Start with the product type you need most, then refine the list inside that category.
          </p>
        </div>
      </section>

      <section className="product-categories">
        <div className="categories-grid">
          {storefrontCategories.map((category) => (
            <button
              key={category.category}
              type="button"
              className="category-card"
              onClick={() => enterCategory(category.category)}
              aria-label={category.title}
            >
              <span className="category-card-accent">{category.accent}</span>
              <h2>{category.title}</h2>
              <p>{category.description}</p>
              <span className="category-arrow">Explore →</span>
            </button>
          ))}
        </div>
      </section>

      <section className="shop-info">
        <div className="info-content">
          <h2>Need help choosing?</h2>
          <p>
            Visit the shop if you want hands-on advice for matching racquet feel, string response,
            and everyday badminton essentials.
          </p>
          <div className="shop-details">
            <p>
              <strong>Location:</strong> 60 Eu Tong Sen St, #01-17 Furama City Center, Singapore
              059804
            </p>
            <p>
              <strong>Before visiting:</strong> Contact +6590688780 so we can prepare the right
              selection for you.
            </p>
          </div>
          <a href="/contact" className="cta-button">
            Contact Us
          </a>
        </div>
      </section>
    </>
  );

  const renderCatalogView = () => (
    <>
      <header className="page-header storefront-page-header">
        <span className="products-kicker">Browse products</span>
        <h1>{categoryTitle}</h1>
        <p>{CATEGORY_INTRO_COPY[selectedCategory] || `Browse our selection of ${selectedCategory}`}</p>
        <button className="back-to-all-btn" onClick={() => setSelectedCategory('all')}>
          ← Back to Category Gateway
        </button>
      </header>

      <div className="filter-sort-toolbar">
        <input
          type="text"
          placeholder="Search by name..."
          value={filterText}
          onChange={(event) => handleFilterChange(event.target.value)}
        />

        {selectedCategory === 'strings' && stringBrands.length > 0 && (
          <select value={brandFilter} onChange={(event) => {
            const value = event.target.value;
            setBrandFilter(value);
            fetchProducts(selectedCategoryRef.current, filterText, sortOptionRef.current, stringTypeFilterRef.current, racquetTypeFilterRef.current, gaugeFilterRef.current, value, 1);
          }}>
            <option value="">All Brands</option>
            {stringBrands.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        )}

        {selectedCategory === 'strings' && (
          <select
            value={stringTypeFilter}
            onChange={(event) => {
              const value = event.target.value;
              setStringTypeFilter(value);
              fetchProducts(selectedCategoryRef.current, filterText, sortOptionRef.current, value, racquetTypeFilterRef.current, gaugeFilterRef.current, brandFilterRef.current, 1);
            }}
          >
            <option value="">All Types</option>
            <option value="Repulsion">Repulsion</option>
            <option value="Control">Control</option>
            <option value="Power">Power</option>
            <option value="Durability">Durability</option>
            <option value="All-round">All-round</option>
          </select>
        )}

        {selectedCategory === 'strings' && (
          <select
            value={racquetTypeFilter}
            onChange={(event) => {
              const value = event.target.value;
              setRacquetTypeFilter(value);
              fetchProducts(selectedCategoryRef.current, filterText, sortOptionRef.current, stringTypeFilterRef.current, value, gaugeFilterRef.current, brandFilterRef.current, 1);
            }}
          >
            <option value="">All Racquet Types</option>
            <option value="Badminton">Badminton</option>
            <option value="Tennis">Tennis</option>
            <option value="Squash">Squash</option>
          </select>
        )}

        {selectedCategory === 'strings' && (
          <select
            value={gaugeFilter}
            onChange={(event) => {
              const value = event.target.value;
              setGaugeFilter(value);
              fetchProducts(selectedCategoryRef.current, filterText, sortOptionRef.current, stringTypeFilterRef.current, racquetTypeFilterRef.current, value, brandFilterRef.current, 1);
            }}
          >
            <option value="">All Thickness</option>
            <option value="0.58mm">0.58mm</option>
            <option value="0.61mm">0.61mm</option>
            <option value="0.62mm">0.62mm</option>
            <option value="0.63mm">0.63mm</option>
            <option value="0.65mm">0.65mm</option>
            <option value="0.66mm">0.66mm</option>
            <option value="0.68mm">0.68mm</option>
            <option value="0.70mm">0.70mm</option>
            <option value="0.72mm">0.72mm</option>
          </select>
        )}

        <select value={sortOption} onChange={(event) => handleSortChange(event.target.value)}>
          <option value="">Default</option>
          <option value="price_asc">Price: Low → High</option>
          <option value="price_desc">Price: High → Low</option>
          <option value="name_asc">Name: A → Z</option>
          <option value="id_desc">Newest First</option>
        </select>
      </div>

      <section className="products-grid">
        {loading ? (
          <p className="no-results">Loading...</p>
        ) : products.length === 0 ? (
          <p className="no-results">No products match your search.</p>
        ) : (
          products.map((product) => (
            <div
              key={product.id}
              className="product-card"
              onClick={() =>
                navigate(
                  product.category === 'strings' ? `/string/${product.id}` : `/product/${product.id}`,
                  { state: { fromCategory: selectedCategory, fromCategoryTitle: categoryTitle } }
                )
              }
            >
              <div className={`product-image${!product.inStock ? ' product-image--oos' : ''}`}>
                {(product.images?.[0] || product.image) ? (
                  <img
                    src={product.images?.[0] || product.image}
                    alt={product.name}
                    className="product-img"
                    onError={(event) => {
                      event.target.style.display = 'none';
                      if (event.target.nextSibling) {
                        event.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                ) : (
                  <div className="image-placeholder">{product.category}</div>
                )}
              </div>
              <div className="product-info">
                {product.category === 'strings' && product.string_type && (
                  <div className={`string-type-badge string-type-badge--${product.string_type.toLowerCase().replace(/\s+/g, '-')}`}>
                    {product.string_type}
                  </div>
                )}
                <h3>{product.name}</h3>
                {product.category === 'strings' && (
                  <div className="string-material-specs">
                    {product.racquet_type && <span className="spec-tag">For: {product.racquet_type}</span>}
                    {product.gauge && <span className="spec-tag">{product.gauge}</span>}
                    {product.coreMaterial && <span className="spec-tag">Core: {product.coreMaterial}</span>}
                    {product.outerMaterial && <span className="spec-tag">Outer: {product.outerMaterial}</span>}
                    {product.coating && product.coating !== 'No' && <span className="spec-tag">Coated</span>}
                  </div>
                )}
                <div className="product-footer">
                  <span className="price">$ {product.price}</span>
                  <span className={`stock-status ${product.inStock ? 'in-stock' : product.out_for_rent ? 'out-for-rent' : 'out-of-stock'}`}>
                    {product.inStock ? 'In Stock' : product.out_for_rent ? 'Out for Rent' : 'Out of Stock'}
                  </span>
                </div>
                {product.category === 'racquets' && product.rentalFee != null && (
                  <div className="rental-fee-badge">Rent 10 days: ${product.rentalFee}</div>
                )}
                {product.category === 'racquets' && product.item_condition && (
                  <div className="condition-badge">Condition: {product.item_condition}</div>
                )}
              </div>
            </div>
          ))
        )}
      </section>

      {selectedCategory === 'strings' && stringTotalPages > 1 && (
        <div className="string-pagination">
          <button
            className="page-btn"
            onClick={() => handleStringPageChange(stringPage - 1)}
            disabled={stringPage <= 1}
          >
            ← Prev
          </button>
          <span className="page-info">
            Page {stringPage} of {stringTotalPages}
          </span>
          <button
            className="page-btn"
            onClick={() => handleStringPageChange(stringPage + 1)}
            disabled={stringPage >= stringTotalPages}
          >
            Next →
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="products-page">
      <div className="container">{selectedCategory === 'all' ? renderCategoryGateway() : renderCatalogView()}</div>
    </div>
  );
};

export default Products;
